/**
 * SynthNode - plays notes using Tone.js PolySynth
 *
 * Endpoints:
 * - event (input): note events to play
 * - volume (input): output volume in dB
 * - octave (input): transposition in octaves (-4 to +4)
 * - oscillator (input): oscillator type
 * - envelope (input): full ADSR envelope
 * - attack, decay, sustain, release (input): envelope params
 *
 * Audio context is passed via dependency injection in initialize()
 */

import { ToneAudioBaseNode } from '../ToneAudioBaseNode';
import { Domain } from '@nodes/Endpoint';
import { NodeDef } from '@runtime/IRuntime';
import { Payload } from '@graph/Payload';
import { EnvelopeData } from '@nodes/EnvelopeData';
import { envelopeEndpoints } from '@audio/sharedEndpoints';

import { SYNTH_PRESETS } from './presets';
import type { ToneJsDependencies } from '../../tonejsPlugin';
import { audio, events, params } from '@/nodes/TypedEndpoints';

const endpoints = {
  ...audio({
    audioOut: {  isOutput: true },
  }),
  ...events({
    event: { isInput: true, primary: true },
  }),
  ...params({
    preset: { dataType: 'string', options: SYNTH_PRESETS.map(p => p.name) },
    volume: { dataType: 'number', value: -12, min: -60, max: 0 },
    defaultPitch: { dataType: 'number', value: 60, min: 0, max: 127, step: 1 },
    defaultLength: { dataType: 'string', value: '8n', options: ['1n', '2n', '4n', '8n', '16n', '32n'] as const },
    defaultVelocity: { dataType: 'number', value: 0.7, min: 0, max: 1, step: 0.01 },
    octave: { dataType: 'number', value: 0, min: -4, max: 4, step: 1 },
    detune: { dataType: 'number', value: 0, min: -1200, max: 1200, step: 1 },
    oscillator: { dataType: 'string', value: 'triangle', options: ['sine', 'triangle', 'square', 'sawtooth'] as const },
    envelope: { dataType: 'envelope', isInput: true },
    ...envelopeEndpoints,
  })
};

export class SynthNode extends ToneAudioBaseNode<typeof endpoints> {
  private synth: any = null;

  constructor(id: string, deps: ToneJsDependencies) {
    super(id, deps);
  }

  initialize(): void {
    super.initialize();

    this.synth = new this.tone.PolySynth(this.tone.Synth, {
      oscillator: { type: this.params.oscillator.value },
      envelope: {
        attack: this.params.attack.value ?? 0.01,
        decay: this.params.decay.value ?? 0.1,
        sustain: this.params.sustain.value ?? 0.5,
        release: this.params.release.value ?? 0.2
      }
    });
    this.synth.volume.value = this.params.volume.value;
    // Set initial detune (in cents)
    if (this.params.detune.value) {
      this.synth.set({ detune: this.params.detune.value });
    }

    // Connect synth to output endpoint (Tone.js bridging handled automatically)
    this.audio.audioOut.connect(this.synth);
  }

  dispose(): void {
    if (this.synth) {
      this.synth.releaseAll();
      this.synth.dispose();
      this.synth = null;
    }
    super.dispose();
  }

  // Disable/enable individual ADSR endpoints when envelope is connected/disconnected
  private setEnvelopeEndpointsDisabled(disabled: boolean): void {
    for (const ep of ['attack', 'decay', 'sustain', 'release']) {
      this.setEndpointDisabled(ep, disabled);
    }
  }

  onConnectedFrom(endpointId: string): void {
    if (endpointId === 'envelope') {
      this.setEnvelopeEndpointsDisabled(true);
    }
  }

  onDisconnectedFrom(endpointId: string): void {
    if (endpointId === 'envelope') {
      this.setEnvelopeEndpointsDisabled(false);
    }
  }

  onParamChanged(payload: Payload, endpointId: string): void {
    this.logTrace(`onParamChanged: ${endpointId} = ${JSON.stringify(payload.value)}`);
    switch (endpointId) {
      case 'volume':
        this.setVolume(payload.value);
        break;
      case 'oscillator':
        this.setOscillator(payload.value);
        break;
      case 'detune':
        this.setDetune(payload.value);
        break;
      case 'envelope':
        this.setFullEnvelope(payload.value);
        break;
      case 'attack':
      case 'decay':
      case 'sustain':
      case 'release':
        this.setEnvelope(endpointId, payload.value);
        break;
    }
  }

  // ─────────────────────────────────────────────────────────────
  // Event Handling
  // ─────────────────────────────────────────────────────────────

  handleEvent(payload: Payload, endpointId: string, source?: any): void {
    // Forward pan metadata to audio outputs (via ToneAudioBaseNode)
    super.handleEvent(payload, endpointId, source);

    this.logTrace(`handleEvent called with: ${JSON.stringify(payload)}`);

    if (!this.synth) {
      throw new Error(`[Synth ${this.id}] synth is null`);
    }

    // Duration: number = ticks, convert to Tone.js format; fallback to defaultLength
    const durationTicks = payload.duration;
    const duration = durationTicks != null ? `${durationTicks}i` : (this.params.defaultLength.value ?? '8n');
    const velocity = payload.velocity ?? this.params.defaultVelocity.value ?? 0.7;
    const time = payload.time ?? this.tone.now();

    // If value is a number, use it as MIDI note. Otherwise use defaultPitch parameter
    const baseNote = typeof payload.value === 'number' ? payload.value : (this.params.defaultPitch.value ?? 60);
    const midiNote = baseNote + (this.params.octave.value * 12);
    // Convert MIDI to note string - Tone.js interprets raw numbers as Hz frequency
    const note = this.tone.Frequency(midiNote, 'midi').toNote();

    this.logTrace(`playing note=${note} (midi=${midiNote}), duration=${duration}, velocity=${velocity}`);

    this.synth.triggerAttackRelease(note, duration, time, velocity);
  }



  // ─────────────────────────────────────────────────────────────
  // Parameter Control
  // ─────────────────────────────────────────────────────────────

  private setVolume(value: number): void {
    if (this.synth) {
      this.synth.volume.value = value;

    }
  }

  private setOscillator(type: string): void {
    if (this.synth) {
      this.synth.set({ oscillator: { type } });
    }
  }

  private setDetune(cents: number): void {
    if (this.synth) {
      this.synth.set({ detune: cents });
    }
  }

  private setEnvelope(param: string, value: number): void {
    if (this.synth) {
      // Tone.js PolySynth doesn't handle partial envelope updates well
      // We need to set the full envelope object with all current values
      const envelope = {
        attack: this.params.attack.value,
        decay: this.params.decay.value,
        sustain: this.params.sustain.value,
        release: this.params.release.value,
        [param]: value  // Override with the new value
      };
      this.synth.set({ envelope });
    }
  }

  private setFullEnvelope(envelope: EnvelopeData): void {
    if (!this.synth) throw new Error(`[${this.id}] setFullEnvelope: synth not initialized`);
    if (!envelope) throw new Error(`[${this.id}] setFullEnvelope: envelope is required`);

    // Merge with current values for partial updates
    const fullEnvelope: EnvelopeData = {
      attack: envelope.attack ?? this.params.attack.value,
      decay: envelope.decay ?? this.params.decay.value,
      sustain: envelope.sustain ?? this.params.sustain.value,
      release: envelope.release ?? this.params.release.value
    };

    // Update store values
    this.params.attack.set(fullEnvelope.attack);
    this.params.decay.set(fullEnvelope.decay);
    this.params.sustain.set(fullEnvelope.sustain);
    this.params.release.set(fullEnvelope.release);

    // Apply to synth
    this.synth.set({ envelope: fullEnvelope });
  }



}

// ─────────────────────────────────────────────────────────────
// Node Definition
// ─────────────────────────────────────────────────────────────

export const synthNodeDef: NodeDef<SynthNode> = {
  type: 'Synth',
  shortName: 'synth',
  info: 'Polyphonic synthesizer using Tone.js. Plays note events with configurable oscillator and ADSR envelope.',
  domain: Domain.Audio,
  endpoints,
  factoryPresets: SYNTH_PRESETS,
  ctor: SynthNode
};
